import { LGraphNode, LiteGraph } from "litegraph.js";
import { analyzeFFT } from "../engine/drivers.js";

/**
 * AudioReactiveNode
 * -----------------
 * Captures microphone input via Web Audio API and outputs:
 *  - Amplitude (0..1) — overall loudness
 *  - Bass (0..1) — low frequency energy
 *  - Treble (0..1) — high frequency energy
 *
 * Wire these outputs to any numeric input (grid count, cell scale,
 * bounce width, timing speed, etc.) to make the animation react to sound.
 */
export class AudioReactiveNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Audio Reactive";

    this.properties = {
      smoothing: 0.8,
      gain: 1,
      active: false,
    };

    this._audioCtx = null;
    this._analyser = null;
    this._source = null;
    this._freqData = null;
    this._amplitude = 0;
    this._bass = 0;
    this._treble = 0;
    this._smoothAmplitude = 0;
    this._smoothBass = 0;
    this._smoothTreble = 0;
    this._error = null;

    this.addWidget("toggle", "Active", this.properties.active, (val) => {
      this.properties.active = val;
      if (val) this._startMic();
      else this._stopMic();
    });

    this.addWidget("slider", "Gain", this.properties.gain, (val) => {
      this.properties.gain = Math.round(val * 100) / 100;
    }, { min: 0.1, max: 5, step: 0.1 });

    this.addWidget("slider", "Smoothing", this.properties.smoothing, (val) => {
      this.properties.smoothing = Math.round(val * 100) / 100;
      if (this._analyser) this._analyser.smoothingTimeConstant = val;
    }, { min: 0, max: 0.99, step: 0.01 });

    this.addOutput("Amplitude", "number");
    this.addOutput("Bass", "number");
    this.addOutput("Treble", "number");

    this.size = [200, 175];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  async _startMic() {
    try {
      this._error = null;
      this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      this._source = this._audioCtx.createMediaStreamSource(stream);
      this._analyser = this._audioCtx.createAnalyser();
      this._analyser.fftSize = 256;
      this._analyser.smoothingTimeConstant = this.properties.smoothing;
      this._source.connect(this._analyser);
      this._freqData = new Uint8Array(this._analyser.frequencyBinCount);
    } catch (e) {
      this._error = e.message || "Microphone access denied";
      this.properties.active = false;
    }
  }

  _stopMic() {
    if (this._source) {
      this._source.disconnect();
      this._source.mediaStream.getTracks().forEach(t => t.stop());
      this._source = null;
    }
    if (this._audioCtx) {
      this._audioCtx.close();
      this._audioCtx = null;
    }
    this._analyser = null;
    this._freqData = null;
    this._smoothAmplitude = 0;
    this._smoothBass = 0;
    this._smoothTreble = 0;
  }

  onRemoved() {
    this._stopMic();
  }

  onDrawForeground(ctx) {
    const w = this.size[0];
    const h = this.size[1];
    const barY = h - 40;
    const barH = 30;
    const pad = 10;

    if (this._error) {
      ctx.fillStyle = "#ff4444";
      ctx.font = "10px sans-serif";
      ctx.fillText(this._error, pad, barY + 12);
      return;
    }

    if (!this._freqData || !this.properties.active) {
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.font = "10px sans-serif";
      ctx.fillText("Toggle Active to start mic", pad, barY + 12);
      return;
    }

    // Draw frequency bars
    const binCount = this._freqData.length;
    const barWidth = (w - pad * 2) / binCount;

    for (let i = 0; i < binCount; i++) {
      const val = this._freqData[i] / 255;
      const barHeight = val * barH;

      // Color: bass=blue, mid=white, treble=pink
      const ratio = i / binCount;
      if (ratio < 0.33) ctx.fillStyle = "rgba(78,158,255,0.6)";
      else if (ratio < 0.66) ctx.fillStyle = "rgba(255,255,255,0.4)";
      else ctx.fillStyle = "rgba(255,78,158,0.6)";

      ctx.fillRect(pad + i * barWidth, barY + barH - barHeight, Math.max(1, barWidth - 1), barHeight);
    }

    // Amplitude bar
    ctx.fillStyle = "#4E9EFF";
    ctx.fillRect(pad, barY - 6, (w - pad * 2) * this._smoothAmplitude, 3);
  }

  onExecute() {
    if (!this._analyser || !this._freqData || !this.properties.active) {
      this.setOutputData(0, 0);
      this.setOutputData(1, 0);
      this.setOutputData(2, 0);
      return;
    }

    this._analyser.getByteFrequencyData(this._freqData);

    const result = analyzeFFT(this._freqData, {
      gain: this.properties.gain,
      smoothing: this.properties.smoothing,
      prev: {
        smoothAmplitude: this._smoothAmplitude,
        smoothBass: this._smoothBass,
        smoothTreble: this._smoothTreble,
      },
    });

    this._amplitude = result.amplitude;
    this._bass = result.bass;
    this._treble = result.treble;
    this._smoothAmplitude = result.smoothAmplitude;
    this._smoothBass = result.smoothBass;
    this._smoothTreble = result.smoothTreble;

    this.setOutputData(0, this._smoothAmplitude);
    this.setOutputData(1, this._smoothBass);
    this.setOutputData(2, this._smoothTreble);
    this.setDirtyCanvas(true);
  }
}

LiteGraph.registerNodeType("generators/AudioReactive", AudioReactiveNode);
